// geoCoords.js — sitrep payload-shape overhaul (MINSKY-16404 design review): flat lat/lon +
// MGRS coordinate fields for the sitrep payload, replacing the raw GeoJSON `{type, coordinates:
// [lon, lat]}` wrapper the payload previously emitted verbatim.
//
// MGRS reuse-audit (per the review's instruction to check for an existing conversion utility
// before writing a new one): `coordGrid.js` and `SymbolEditor.jsx` both already import the `mgrs`
// npm package (`import * as mgrsLib from 'mgrs'`) and call `mgrsLib.forward([lon, lat], accuracy)`
// — this module does the same rather than adding a second MGRS implementation.

import * as mgrsLib from 'mgrs';

const COORD_DECIMALS = 5; // ~1m precision, per the review's "round to 5 decimals" guidance.
const MGRS_ACCURACY_DIGITS = 5; // 1m — matches SymbolEditor.jsx's `lonLatToMgrsString` precision.

/**
 * Round a coordinate value to `COORD_DECIMALS` places. Non-finite/nullish input passes through
 * unchanged (never throws) so callers can round optional fields without a guard at every call
 * site.
 *
 * @param {number|null|undefined} value
 * @returns {number|null|undefined}
 */
export function roundCoord(value) {
    if (typeof value !== 'number' || !Number.isFinite(value)) return value;
    return Math.round(value * 10 ** COORD_DECIMALS) / 10 ** COORD_DECIMALS;
}

/**
 * Convert a lat/lon pair to an MGRS grid string, reusing the `mgrs` package (see this module's
 * doc comment) rather than a new implementation. Never throws — `mgrs.forward` throws on
 * out-of-range input (e.g. polar regions below 80°S/above 84°N — see SymbolEditor.jsx's own
 * comment to this effect), so this wraps that the same way `coordGrid.js`/`SymbolEditor.jsx` do.
 *
 * @param {number} lat
 * @param {number} lon
 * @returns {string|null}
 */
export function latLonToMgrs(lat, lon) {
    if (typeof lat !== 'number' || typeof lon !== 'number') return null;
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
    try {
        return mgrsLib.forward([lon, lat], MGRS_ACCURACY_DIGITS);
    } catch (_) {
        return null;
    }
}

/**
 * Flatten a GeoJSON Point geometry's `[lon, lat]` coordinates into a `{lat, lon}` pair, rounded
 * to 5 decimals. Only Point geometries are handled — this sitrep payload's unit entries are
 * always single-point positions (see buildSitrepPayload.js); other geometry types (phase lines,
 * polygons) keep their existing geometry representation, unaffected by this flattening.
 *
 * @param {{type?: string, coordinates?: unknown}|null|undefined} geometry
 * @returns {{lat: number, lon: number}|null}
 */
export function pointGeometryToLatLon(geometry) {
    if (!geometry || geometry.type !== 'Point') return null;
    const coords = geometry.coordinates;
    if (!Array.isArray(coords) || coords.length < 2) return null;
    const [lon, lat] = coords;
    if (typeof lon !== 'number' || typeof lat !== 'number') return null;
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) return null;
    return { lat: roundCoord(lat), lon: roundCoord(lon) };
}
